import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AiOutlineClose } from 'react-icons/ai';
import { FiType, FiAlignLeft, FiMail, FiPaperclip, FiFileText, FiList } from 'react-icons/fi';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { componentTypes, initialComponentState } from './constants';

const ComponentDialog = ({
  open,
  onOpenChange,
  componentData,
  onComponentChange,
  onAddComponent,
  isEditing,
  errors = {},
}) => {
  const [optionInput, setOptionInput] = useState('');

  const getIconComponent = (iconName) => {
    const iconMap = {
      FiType: <FiType className="mr-2" />,
      FiAlignLeft: <FiAlignLeft className="mr-2" />,
      FiMail: <FiMail className="mr-2" />,
      FiPaperclip: <FiPaperclip className="mr-2" />,
      FiFileText: <FiFileText className="mr-2" />,
      FiList: <FiList className="mr-2" />
    };
    
    return iconMap[iconName] || null;
  };

  const addOption = () => {
    if (!optionInput.trim()) return;
    
    onComponentChange('options', [...(componentData.options || []), optionInput.trim()]);
    setOptionInput('');
  };

  const removeOption = (index) => {
    onComponentChange('options', componentData.options.filter((_, i) => i !== index));
  };

  const handleComponentTypeChange = (type) => {
    // Reset the form when changing type
    onComponentChange('type', type, true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">
            {isEditing ? 'Edit Component' : 'Add New Component'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="space-y-3">
            <Label htmlFor="componentType">Component Type</Label>
            <Select 
              value={componentData.type} 
              onValueChange={handleComponentTypeChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select component type" />
              </SelectTrigger>
              <SelectContent>
                {componentTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center">
                      {getIconComponent(type.icon)}
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {componentData.type !== 'markdown' && (
            <div className="space-y-3">
              <Label htmlFor="componentName">Label</Label>
              <Input 
                id="componentName"
                value={componentData.name}
                onChange={(e) => onComponentChange('name', e.target.value)}
                placeholder="Describe the issue"
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name}</p>
              )}
            </div>
          )}
          
          {componentData.type !== 'markdown' && componentData.type !== 'file' && (
            <div className="space-y-3">
              <Label htmlFor="placeholder">Placeholder</Label>
              <Input 
                id="placeholder"
                value={componentData.placeholder}
                onChange={(e) => onComponentChange('placeholder', e.target.value)}
                placeholder="Briefly describe what happened..."
              />
            </div>
          )}
          
          {componentData.type === 'markdown' && (
            <div className="space-y-3 md:col-span-2">
              <Label htmlFor="content">Markdown Content</Label>
              <Textarea 
                id="content"
                value={componentData.content}
                onChange={(e) => onComponentChange('content', e.target.value)}
                placeholder="# Please report any issues below\nTell us what happened and how to reproduce it."
                className={`min-h-[100px] ${errors.content ? 'border-red-500' : ''}`}
              />
              {errors.content && (
                <p className="text-sm text-red-500">{errors.content}</p>
              )}
            </div>
          )}
          
          {componentData.type === 'select' && (
            <div className="space-y-3 md:col-span-2">
              <div className="flex flex-col space-y-2">
                <Label htmlFor="options">Options</Label>
                <div className="flex space-x-2">
                  <Input
                    id="options"
                    value={optionInput}
                    onChange={(e) => setOptionInput(e.target.value)}
                    placeholder="Add an option..."
                    className="flex-1"
                  />
                  <Button 
                    type="button" 
                    onClick={addOption}
                    variant="outline"
                  >
                    Add
                  </Button>
                </div>
                {errors.options && (
                  <p className="text-sm text-red-500">{errors.options}</p>
                )}
              </div>
              
              {componentData.options && componentData.options.length > 0 ? (
                <div className="bg-muted p-3 rounded-md">
                  <p className="text-sm text-muted-foreground mb-2">Options:</p>
                  <div className="space-y-2">
                    {componentData.options.map((option, index) => (
                      <div key={index} className="flex justify-between items-center bg-card p-2 rounded border">
                        <span>{option}</span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeOption(index)}
                          className="text-red-500 h-8 w-8 p-0"
                        >
                          <AiOutlineClose className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No options added yet</p>
              )}
            </div>
          )}
          
          {componentData.type !== 'markdown' && (
            <div className="space-y-3 md:col-span-2 mt-2 pt-2 border-t">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium">Field Options</h3>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="required"
                  checked={componentData.required}
                  onCheckedChange={(checked) => onComponentChange('required', checked)}
                />
                <Label htmlFor="required" className="font-normal">Required field</Label>
              </div>
              
              {(componentData.type === 'text' || componentData.type === 'email') && (
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="rememberValue"
                    checked={componentData.rememberValue}
                    onCheckedChange={(checked) => onComponentChange('rememberValue', checked)}
                  />
                  <Label htmlFor="rememberValue" className="font-normal flex items-center">
                    Remember user input
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="ml-1 inline-flex items-center justify-center rounded-full bg-muted px-1 text-xs h-4 w-4">?</span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>Saves the user's input locally so they don't have to re-enter it if they use this form again. Great for contact information fields!</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                </div>
              )}
            </div>
          )}
          
          {componentData.type === 'file' && (
            <div className="space-y-4 md:col-span-2 mt-2 pt-2 border-t">
              <h3 className="text-sm font-medium">File Upload Settings</h3>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="multipleFiles"
                    checked={componentData.multipleFiles}
                    onCheckedChange={(checked) => onComponentChange('multipleFiles', checked)}
                  />
                  <Label htmlFor="multipleFiles" className="font-normal">Allow multiple files</Label>
                </div>
                
                {componentData.multipleFiles && (
                  <div className="space-y-2">
                    <Label htmlFor="maxFiles">Maximum Files</Label>
                    <Input
                      id="maxFiles"
                      type="number"
                      min="1"
                      max="20"
                      value={componentData.maxFiles || 5}
                      onChange={(e) => onComponentChange('maxFiles', parseInt(e.target.value) || 5)}
                    />
                    <p className="text-xs text-muted-foreground">Limit the number of files users can upload</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter className="mt-6">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button onClick={onAddComponent}>
            {isEditing ? 'Save Changes' : 'Add Component'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ComponentDialog; 